import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  CardGiftcard,
  CheckCircle,
  Launch,
  PlayCircleOutline,
} from '@mui/icons-material';
import { useStatsigClient } from '@statsig/react-bindings';
import { EntitlementUsage, useEntitlement } from '@features/entitlements';
import { useSubscription } from '@features/subscriptions';
import { useUserRoles } from '@features/user';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { DynamicConfigs } from '@services/statsig/constants';
import { publicAsset } from '@utils/publicAsset';
import { ROLES } from '@utils/roleUtils';

const DEFAULT_CHROME_WEB_STORE_URL =
  'https://chromewebstore.google.com/detail/jotpsych-ehr-extension/jooogmdiklogejmlncnknjembkilajdb';

function useEhrExtensionStoreUrl(): string {
  const { client } = useStatsigClient();
  return useMemo(() => {
    const config = client.getDynamicConfig(DynamicConfigs.EHR_EXTENSION);
    const url = config.value?.chrome_web_store_url;
    return typeof url === 'string' && url.length > 0 ? url : DEFAULT_CHROME_WEB_STORE_URL;
  }, [client]);
}

function useEhrExtensionUrls() {
  const { client } = useStatsigClient();
  return useMemo(() => {
    const config = client.getDynamicConfig(DynamicConfigs.EHR_EXTENSION_URLS);
    const value = config.value as Partial<{ howToUrl: string }>;
    return {
      howToUrl:
        typeof value.howToUrl === 'string' && value.howToUrl.length > 0
          ? value.howToUrl
          : null,
    };
  }, [client]);
}

const CAPABILITY_NAME = 'ehr_note_copy';

type ViewState = 'not-installed' | 'installed-free' | 'installed-unlimited';

/**
 * Determines which view to render based on entitlement state.
 */
function getViewState(
  entitlement: ReturnType<typeof useEntitlement>['entitlement'],
): ViewState {
  if (!entitlement) return 'not-installed';
  if (entitlement.quota === null || entitlement.quota === undefined) return 'installed-unlimited';
  // Use current_usage > 0 as a proxy for "has installed and used the extension"
  if ((entitlement.current_usage ?? 0) > 0) return 'installed-free';
  return 'not-installed';
}

const VALUE_PROPS = [
  {
    title: 'Side-by-side view',
    description: 'See your notes and EHR side-by-side',
  },
  {
    title: 'One-click transfer',
    description: 'One-click transfer — no more switching tabs',
  },
  {
    title: 'Multi-EHR support',
    description: 'Works with SimplePractice, DrChrono, and other EHRs',
  },
];


const HOW_TO_STEPS = [
  'Open an empty note in your EHR',
  'Open JotTransfer from your Chrome toolbar',
  'Select a note. Click "Launch" to transfer',
];

/**
 * View shown when user hasn't installed the extension (no entitlement).
 */
const NotInstalledView: React.FC<{ quota: number | null | undefined }> = ({ quota }) => {
  const chromeWebStoreUrl = useEhrExtensionStoreUrl();
  const { subscription } = useSubscription();
  const { ehrExtension } = useEventTracking();
  const isTrialing = subscription?.subscription_status === 'trialing';
  const freeQuota = quota ?? 15;

  const handleAddToChrome = () => {
    ehrExtension.chromeStoreClicked({ source: 'settings_not_installed' });
    window.open(chromeWebStoreUrl, '_blank', 'noopener,noreferrer');
  };

  return (
  <Stack spacing={3}>
    {/* CTA banner */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        bgcolor: 'grey.100',
        borderRadius: 2,
        px: 3,
        py: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <CardGiftcard sx={{ fontSize: 24, color: 'grey.600' }} />
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {isTrialing
              ? `Included in your trial — ${freeQuota} notes`
              : `Start free with ${freeQuota} notes`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isTrialing
              ? 'Transfer notes directly into your EHR while you explore JotPsych'
              : 'Try the extension at no cost — upgrade anytime for unlimited use'}
          </Typography>
        </Box>
      </Stack>
      <Button
        variant="contained"
        endIcon={<ArrowForward />}
        onClick={handleAddToChrome}
        sx={{ borderRadius: 6, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        Add to Chrome
      </Button>
    </Box>

    {/* How-to gif */}
    <Box
      component="img"
      src={publicAsset('/new-feature-images/ehr-extension-preview.gif')}
      alt="How to open JotTransfer"
      sx={{
        width: '100%',
        objectFit: 'contain',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    />

    {/* Value props grid */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: 3,
      }}
    >
      {VALUE_PROPS.map(prop => (
        <Card
          key={prop.title}
          variant="outlined"
          sx={{ p: 2.5, textAlign: 'center' }}
        >
          <CheckCircle sx={{ fontSize: 28, color: 'success.main', mb: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {prop.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {prop.description}
          </Typography>
        </Card>
      ))}
    </Box>
  </Stack>
  );
};

/**
 * How-to steps + optional tutorial video link, shared between free and unlimited views.
 */
const HowToUseSection: React.FC<{ howToUrl: string | null }> = ({ howToUrl }) => (
  <Box>
    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
      How to use
    </Typography>
    <Stack spacing={1.5}>
      {HOW_TO_STEPS.map((step, i) => (
        <Stack key={i} direction="row" alignItems="flex-start" spacing={1.5}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'action.selected',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" fontWeight={600}>
              {i + 1}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {step}
          </Typography>
        </Stack>
      ))}
      {howToUrl && (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ pt: 0.5 }}>
          <PlayCircleOutline sx={{ fontSize: 18, color: 'primary.main' }} />
          <Link
            href={howToUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            underline="hover"
          >
            Watch tutorial video
          </Link>
        </Stack>
      )}
    </Stack>
  </Box>
);

/**
 * View shown when user has a metered (free tier) entitlement.
 */
const InstalledFreeView: React.FC = () => {
  const { howToUrl } = useEhrExtensionUrls();

  return (
    <Stack spacing={3}>
      {/* Usage meter */}
      <EntitlementUsage capabilityName={CAPABILITY_NAME} source="ehr_extension_card" />

      {/* How to use */}
      <HowToUseSection howToUrl={howToUrl} />
    </Stack>
  );
};

/**
 * View shown when user has an unlimited entitlement (paid).
 * Practice admins see a CTA to manage clinicians; non-admins see no CTA.
 */
const InstalledUnlimitedView: React.FC = () => {
  const navigate = useNavigate();
  const roles = useUserRoles();
  const isPracticeAdmin = roles.includes(ROLES.PRACTICE_ADMINISTRATOR);
  const { ehrExtension } = useEventTracking();
  const { howToUrl } = useEhrExtensionUrls();

  const handleManageClinicians = () => {
    ehrExtension.manageCliniciansClicked();
    navigate('/practice/clinicians');
  };

  return (
    <Stack spacing={3}>
      {/* Status banner */}
      <Alert
        severity="success"
        icon={<CheckCircle />}
        action={
          <Chip label="Unlimited" size="small" color="success" />
        }
      >
        JotTransfer is installed with unlimited transfers
      </Alert>

      {/* How to use */}
      <HowToUseSection howToUrl={howToUrl} />

      {/* Manage subscription — only for practice admins */}
      {isPracticeAdmin && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            px: 2.5,
            py: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Manage your subscription in the Your Practice tab
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Launch />}
            onClick={handleManageClinicians}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Manage Clinicians
          </Button>
        </Box>
      )}
    </Stack>
  );
};

/**
 * EHR Extension integration card for the Account Settings > Integrations page.
 *
 * Shows one of three views based on entitlement state:
 * - NotInstalledView: preview + value props + CTA to install
 * - InstalledFreeView: usage meter + upgrade CTA + how-to
 * - InstalledUnlimitedView: usage stats + how-to
 *
 * Includes a demo state switcher footer for QA testing.
 */
export const EhrExtensionCard: React.FC = () => {
  const isGateEnabled = useFeatureGate('feature_ehr_extension_rollout');
  const { entitlement, isLoading } = useEntitlement(CAPABILITY_NAME);
  const { ehrExtension } = useEventTracking();
  const [demoOverride, setDemoOverride] = useState<ViewState | null>(null);

  const naturalView = getViewState(entitlement);
  const activeView = demoOverride ?? naturalView;

  useEffect(() => {
    if (isGateEnabled && !isLoading) {
      ehrExtension.settingsViewed({ view_state: naturalView });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGateEnabled, isLoading]);

  if (!isGateEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
      {/* Header */}
      <Typography variant="h6" gutterBottom>
        JotTransfer
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box>
        {/* Active view */}
        {activeView === 'not-installed' && <NotInstalledView quota={entitlement?.quota} />}
        {activeView === 'installed-free' && <InstalledFreeView />}
        {activeView === 'installed-unlimited' && <InstalledUnlimitedView />}
      </Box>

      {/* Demo state switcher for QA — dev only */}
      {import.meta.env.DEV && <Box
        sx={{
          pt: 2,
          mt: 2,
          borderTop: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Demo: Preview different states
        </Typography>
        <Stack direction="row" spacing={1}>
          {([
            { value: null, label: `Live (${naturalView})` },
            { value: 'not-installed' as const, label: 'Not installed' },
            { value: 'installed-free' as const, label: 'Free tier' },
            { value: 'installed-unlimited' as const, label: 'Unlimited' },
          ] as const).map(option => (
            <Button
              key={option.label}
              size="small"
              variant={demoOverride === option.value ? 'contained' : 'outlined'}
              onClick={() => setDemoOverride(option.value)}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
      </Box>}
    </Card>
  );
};

EhrExtensionCard.displayName = 'EhrExtensionCard';
